//app.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { graphLoginRequest, dbLoginRequest } from './msAuthConfig';
import Header from './Components/Header';
import Navbar from './Components/Navbar';
import Shipping from './Shipping';
import ShippingItems from './ShippingItems';
import EditLocationEntry from './EditLocationEntry';
import ViewLocationEntry from './ViewLocationEntry';
import './GlobalStyles.css';
import axios from 'axios';
import Home from './Home';
import CreateDocument from './CreateDocument';
import CreateShippingItem from './CreateShippingItem';
import CreateLocationEntry from './CreateLocationEntry';
import useInactivityLogout from './hooks/useInactivityLogout';
import ViewLocationEntryDocuments from './ViewLocationEntryDocuments';
import ViewShippingItem from './ViewShippingItem';
import EditShippingItem from './EditShippingItem';
import ProjectManagerLookup from './ProjectManagerLookup';
import Calibration from './Calibration';
import CalibrationDevices from './CalibrationDevices';
import ViewCalibrationDevice from './ViewCalibrationDevice';
import EditCalibrationDevice from './EditCalibrationDevice';
import AdminTools from './AdminTools';
import { hasRoleAccess } from './utils/roles';

const App = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useInactivityLogout();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [hasAccess, setHasAccess] = useState(false);

  // Set user role and access based on role
  useEffect(() => {
    if (userRole.length < 1) {
      setUserRole('No Access');
    }
    if (userRole === 'Admin' || userRole === 'User' || userRole === 'Employee') {
      setHasAccess(true);
    } else {
      setHasAccess(false);
    }
  }, [userRole]);

  // Setup Axios interceptors and fetch user data and role
  useEffect(() => {
    // Add request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        if (!config.headers['Authorization']) {
          const tokenResponse = await instance.acquireTokenSilent({
            ...dbLoginRequest,
            account: accounts[0],
          });
          config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...dbLoginRequest,
              account: accounts[0],
            });
            axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            return axios(originalRequest);
          } catch (tokenError) {
            console.error('Token renewal failed', tokenError);
            // Instead of showing a login popup, log the user out.
            handleLogout();
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );

    if (isAuthenticated) {
      const request = {
        ...graphLoginRequest,
        account: accounts[0],
      };

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          const token = response.accessToken;
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          // Fetch user data and role after token acquisition
          fetchUserData(token);
          fetchUserRole();
        })
        .catch((error) => {
          console.error('Silent token acquisition failed', error);
          // Instead of a popup, log the user out so the UI appears logged out.
          handleLogout();
        });
    }

    // Cleanup interceptors when component unmounts
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isAuthenticated, accounts, instance]);

  // Debugging: Log current userName and close menu when not authenticated
  useEffect(() => {
    console.log('Current userName:', userName);
    if (!isAuthenticated) {
      setIsMenuOpen(false);
    }
  }, [isAuthenticated, userName]);

  // Fetch user data from Microsoft Graph API
  const fetchUserData = (token) => {
    fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        console.log('User data fetch response:', response);
        return response.json();
      })
      .then((data) => {
        console.log('Fetched user data:', data);
        setUserName(data.displayName);
      })
      .catch((error) => console.error('Error fetching user data:', error));
  };

  // Fetch user role from backend API
  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/user-info`);
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  // Handle user login using redirect (no popup)
  const handleLogin = () => {
    clearInteractionInProgress();
    instance.loginRedirect(graphLoginRequest);
  };

  // Handle user logout using redirect (no popup)
  const handleLogout = () => {
    instance
      .logoutRedirect({ account: accounts[0] })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setUserName('');
        setUserRole('');
        setIsMenuOpen(false);
      });
  };

  // Toggle menu open/close state
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Clear interaction state in local storage
  const clearInteractionInProgress = () => {
    try {
      localStorage.removeItem('msal.interaction.status');
    } catch (error) {
      console.error('Error clearing interaction state:', error);
    }
  };


  return (
    <Router>
      <div className="App">
        <Header
          userName={userName}
          userRole={userRole} // Pass the user role to the Header
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          isAuthenticated={isAuthenticated}
          hasAccess={hasAccess}
          toggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />
        {isAuthenticated && (
          <Navbar isOpen={isMenuOpen} toggleMenu={toggleMenu} handleLogout={handleLogout} userRole={userRole} />
        )}
        <main>
          <Routes>
            <Route path="/" element={<Home userName={userName} />} />

            {/* Shipping Routes */}
            <Route path="/shipping" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="shipping-read"><Shipping /></ProtectedRoute>} />
            <Route path="/shipping-items" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="shipping-read"><ShippingItems /></ProtectedRoute>} />
            <Route path="/shipping-item/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="shipping-read"><ViewShippingItem /></ProtectedRoute>} />
            <Route path="/edit-shipping-item/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="shipping-update"><EditShippingItem /></ProtectedRoute>} />
            <Route path="/create-shipping-item" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="shipping-create"><CreateShippingItem userName={userName} /></ProtectedRoute>} />

            {/* Location Entry Routes */}
            <Route path="/edit-location-entry/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="location-update"><EditLocationEntry /></ProtectedRoute>} />
            <Route path="/view-location-entry/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="location-read"><ViewLocationEntry /></ProtectedRoute>} />
            <Route path="/create-location-entry" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="location-create"><CreateLocationEntry /></ProtectedRoute>} />
            <Route path="/create-location-entry/:shippingID" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="location-create"><CreateLocationEntry /></ProtectedRoute>} />
            <Route path="/location-entry-documentation/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="location-read"><ViewLocationEntry /></ProtectedRoute>} />

            {/* Documents */}
            <Route path="/create-document" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="document-create"><CreateDocument /></ProtectedRoute>} />

            {/* Calibration Routes */}
            <Route path="/calibration" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="calibration"><Calibration /></ProtectedRoute>} />
            <Route path="/calibration-devices" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="calibration"><CalibrationDevices /></ProtectedRoute>} />
            <Route path="/calibration-device/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="calibration"><ViewCalibrationDevice /></ProtectedRoute>} />
            <Route path="/edit-calibration-device/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="calibration"><EditCalibrationDevice /></ProtectedRoute>} />

            {/* Other Routes */}
            <Route path="/pnl" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="project-read"><ProjectManagerLookup /></ProtectedRoute>} />
            <Route path="/admin-tools" element={<ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredRole="admin-tools"><AdminTools /></ProtectedRoute>} />
          </Routes>

        </main>
      </div>
    </Router>
  );
};

// Protected route component to restrict access based on authentication and role
const ProtectedRoute = ({ children, isAuthenticated, userRole, requiredRole }) => {
  if (!isAuthenticated) return <Navigate to="/" />;
  if (!hasRoleAccess(userRole, requiredRole)) return <Navigate to="/" />;
  return React.cloneElement(children, { userRole });
};

export default App;